import axios from 'axios';
import { isEmpty } from 'lodash';

import {
    GetAccountSDKTokenResponseBody,
    GetAccountSnapResponseBody,
    GetAccountSnapSdkCredentialsResponseBody,
    GetAccountSnapTemplateResponseBody,
    ListAccountSnapTemplatesResponseBody,
    ListAccountSnapsResponseBody,
    ListRawGlobalSnapTemplatesResponseBody,
} from '@sparkplug/lib';

import { useAdvancedQuery, useQueries, useQueryClient } from '@hooks/QueryHooks';

const API = {
    getStorifyMeSDKToken: async (accountId: string) => {
        return (
            await axios.get<GetAccountSDKTokenResponseBody>(
                `/api/v1/accounts/${accountId}/storifyme-sdk-token`,
            )
        ).data;
    },
    getSnapTemplate: async ({
        accountId,
        snapTemplateId,
    }: {
        accountId: string;
        snapTemplateId: number;
    }) => {
        return (
            await axios.get<GetAccountSnapTemplateResponseBody>(
                `/api/v1/accounts/${accountId}/snap-templates/${snapTemplateId}`,
            )
        ).data;
    },
    getAccountSnapSdkCredentials: async ({ accountId }: { accountId: string }) => {
        return (
            await axios.get<GetAccountSnapSdkCredentialsResponseBody>(
                `/api/v1/accounts/${accountId}/snaps-sdk`,
            )
        ).data;
    },
    getSparkSnaps: async ({ accountId }: { accountId: string }) => {
        return (
            await axios.get<ListAccountSnapsResponseBody>(`/api/v1/accounts/${accountId}/snaps`)
        ).data;
    },
    getSnap: async ({ accountId, snapId }: { accountId: string; snapId: number }) => {
        return (
            await axios.get<GetAccountSnapResponseBody>(
                `/api/v1/accounts/${accountId}/snaps/${snapId}`,
            )
        ).data;
    },
    getSnapTemplates: async ({ accountId }: { accountId: string }) => {
        return (
            await axios.get<ListAccountSnapTemplatesResponseBody>(
                `/api/v1/accounts/${accountId}/snap-templates`,
            )
        ).data;
    },
    listGlobalSnapTemplates: async () => {
        return (
            await axios.get<ListRawGlobalSnapTemplatesResponseBody>(
                '/api/v1/snaps/global-templates',
            )
        ).data;
    },
};

export const getSnapTemplateKey = (accountId: string, snapTemplateId: number) => [
    'snap-template',
    accountId,
    snapTemplateId,
];
export const useSnapTemplateQuery = ({
    accountId,
    snapTemplateId,
    enabled = true,
}: {
    accountId: string;
    snapTemplateId: number;
    enabled?: boolean;
}) => {
    const {
        isFetched: snapTemplateIsReady,
        data,
        isFetching,
    } = useAdvancedQuery(
        getSnapTemplateKey(accountId, snapTemplateId),
        () =>
            API.getSnapTemplate({
                accountId,
                snapTemplateId,
            }),
        { enabled },
    );

    return {
        snapTemplateIsReady,
        snapTemplate: data?.snapTemplate,
        snapTemplateIsLoading: isFetching,
    };
};

export const getStorifyMeSDKTokenKey = (accountId: string) => ['storify-me-token', accountId];
export const useStorifyMeSDKTokenQuery = (accountId: string) => {
    const queryClient = useQueryClient();

    const { isFetched: storifyMeTokenIsReady, data } = useAdvancedQuery(
        getStorifyMeSDKTokenKey(accountId),
        () => API.getStorifyMeSDKToken(accountId),
        {
            enabled: !!accountId,
            /**
             * In order to insure that we always have a valid token,
             * we want to refresh the storifyme sdk token when msUntilExpiration is reached.
             */
            onSuccess: (d) => {
                queryClient.setQueryDefaults(getStorifyMeSDKTokenKey(accountId), {
                    refetchOnWindowFocus: true,
                    refetchInterval: d.msUntilExpiration,
                    refetchIntervalInBackground: true,
                    cacheTime: d.msUntilExpiration,
                    staleTime: d.msUntilExpiration,
                });
            },
        },
    );

    return {
        storifyMeTokenIsReady,
        storifyMeSDKToken: data?.token,
    };
};

export const useAccountSnapSdkQuery = (
    { accountId }: { accountId: string },
    isEnabled: boolean = true,
) => {
    const { isFetched: accountSnapSdkCredentialsAreReady, data } = useAdvancedQuery(
        ['snap-sdk-credentials', accountId],
        () =>
            API.getAccountSnapSdkCredentials({
                accountId,
            }),
        {
            enabled: isEnabled,
        },
    );

    return { accountSnapSdkCredentialsAreReady, accountSnapSdkCredentials: data };
};

export const getAccountSparkSnapsQueryKey = (accountId: string) => ['spark-snaps', accountId];
export const useAccountSparkSnapsQuery = (
    { accountId }: { accountId: string },
    isEnabled: boolean = true,
) => {
    const { isFetched: accountSparkSnapsAreReady, data } = useAdvancedQuery(
        getAccountSparkSnapsQueryKey(accountId),
        () =>
            API.getSparkSnaps({
                accountId,
            }),
        {
            enabled: isEnabled,
        },
    );

    return { accountSparkSnapsAreReady, accountSparkSnaps: data?.snaps };
};

export const getSnapQueryKey = (accountId: string, snapId: number) => ['snap', accountId, snapId];
export const useSnapQuery = (
    { accountId, snapId }: { accountId: string; snapId: number },
    isEnabled: boolean = true,
) => {
    const { isFetched: snapIsReady, data } = useAdvancedQuery(
        getSnapQueryKey(accountId, snapId),
        () =>
            API.getSnap({
                accountId,
                snapId,
            }),
        {
            enabled: isEnabled,
        },
    );

    return { snapIsReady, snap: data?.snap, snapThumbnail: data?.thumbnailUrl };
};

export const useSnapsById = (
    { accountId, snapIds }: { accountId: string; snapIds: number[] },
    isEnabled = true,
) => {
    const results = useQueries(
        snapIds.map((snapId) => {
            return {
                queryKey: getSnapQueryKey(accountId, snapId),
                queryFn: () =>
                    API.getSnap({
                        accountId,
                        snapId,
                    }),
                isEnabled,
            };
        }),
    );

    const snapsAreReady = results.every((result) => result.isFetched);
    const snaps = results.map((result) => result.data?.snap).filter((snap) => !isEmpty(snap));

    return { snapsAreReady, snaps };
};

export const getSnapTemplatesQueryKey = (accountId: string) => ['snap-templates', accountId];
export const useSnapTemplatesQuery = ({ accountId }: { accountId: string }, isEnabled = true) => {
    const { isFetched: snapTemplatesAreReady, data } = useAdvancedQuery(
        getSnapTemplatesQueryKey(accountId),
        () =>
            API.getSnapTemplates({
                accountId,
            }),
        {
            enabled: isEnabled,
        },
    );

    return { snapTemplatesAreReady, snapTemplates: data?.snapTemplates };
};

export const getGlobalSnapTemplatesQueryKey = 'global-snap-templates';
export const useGlobalSnapTemplatesQuery = () => {
    const { isFetched: globalSnapTemplatesAreReady, data } = useAdvancedQuery(
        getGlobalSnapTemplatesQueryKey,
        API.listGlobalSnapTemplates,
    );

    return { globalSnapTemplatesAreReady, globalSnapTemplates: data?.globalTemplates };
};
